import { readLive, type Rules } from "./arithmetic";
import {
  MAX_EXPRESSION,
  type CountdownDefinition,
  type CountdownProgress,
  type CountdownSubmission,
} from "./types";

export const COUNTDOWN_RULES = {
  en: "Reach the target by combining the numbers with +, −, × and ÷ and parentheses. Each number may be used at most once, and every intermediate result must be a positive whole number.",
  fr: "Atteignez le compte en combinant les nombres avec +, −, × et ÷ et des parenthèses. Chaque nombre sert au plus une fois, et chaque résultat intermédiaire doit être un entier positif.",
};

export const TWENTY_FOUR_RULES = {
  en: "Make the target with +, −, × and ÷ and parentheses, using each number exactly once. Intermediate results may be fractions.",
  fr: "Atteignez le compte avec +, −, × et ÷ et des parenthèses, en utilisant chaque nombre exactement une fois. Les résultats intermédiaires peuvent être des fractions.",
};

export function emptyDefinition(): CountdownDefinition {
  return {
    puzzleType: "countdown",
    typeVersion: 1,
    numbers: [25, 50, 75, 100, 3, 6],
    target: 952,
    useAll: false,
    allowFractions: false,
    rulesText: { ...COUNTDOWN_RULES },
  };
}

export function rulesOf(definition: CountdownDefinition): Rules {
  return {
    numbers: definition.numbers ?? [],
    target: definition.target,
    useAll: definition.useAll ?? false,
    allowFractions: definition.allowFractions ?? false,
  };
}

export function emptyProgress(_definition: CountdownDefinition): CountdownProgress {
  return { version: 1, expression: "" };
}

/** Accepts any stored blob (older versions, null, garbage) and returns a valid progress. */
export function migrateProgress(_definition: CountdownDefinition, raw: unknown): CountdownProgress {
  const expression =
    typeof raw === "object" && raw !== null && !Array.isArray(raw)
      ? (raw as { expression?: unknown }).expression
      : undefined;
  return {
    version: 1,
    expression: typeof expression === "string" ? expression.slice(0, MAX_EXPRESSION) : "",
  };
}

export function serializeSubmission(
  _definition: CountdownDefinition,
  progress: CountdownProgress,
): CountdownSubmission {
  return { expression: progress.expression };
}

/**
 * How many copies of each given number the expression already uses, tile by tile: `used[i]`
 * tells whether tile `i` is taken. Copies are taken left to right, like the server counts.
 */
export function usedTiles(numbers: readonly number[], expression: string): boolean[] {
  const counts = new Map<string, number>();
  for (const match of expression.matchAll(/[0-9]+/g)) {
    const key = BigInt(match[0]).toString();
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  return numbers.map((number) => {
    const key = String(number);
    const left = counts.get(key) ?? 0;
    if (left === 0) return false;
    counts.set(key, left - 1);
    return true;
  });
}

/** Live reading of the player's own expression (value and broken rules). */
export function liveReading(definition: CountdownDefinition, progress: CountdownProgress) {
  return readLive(progress.expression, rulesOf(definition));
}

/** Every run of digits in the field, as numbers; anything else separates them. */
export function parseNumbers(raw: string): number[] {
  return Array.from(raw.matchAll(/[0-9]+/g), (m) => Number.parseInt(m[0], 10));
}
